import { Badge, Button, Modal, cn } from '@levitas/ui';
import {
  Building2,
  CalendarClock,
  Bell,
  CalendarCheck,
  CalendarDays,
  ChevronDown,
  Home,
  LogOut,
  Megaphone,
  Menu,
  MessageCircle,
  Moon,
  Music,
  LayoutGrid,
  ScrollText,
  Settings,
  Sun,
  Users,
  Baby,
  BarChart3,
  DoorOpen,
  CreditCard,
  ShieldAlert,
  ShieldCheck,
  Church,
  Accessibility,
  CircleHelp,
} from 'lucide-react';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import type { PermissionKey, RecursoDoPlano } from '@levitas/contracts';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSessao } from '../../lib/usar-sessao';
import { alternarTema, aplicarCorDaMarca, temaAtual, type Tema } from '../../lib/tema';
import { FaixaDaAssinatura } from './faixa-da-assinatura';
import { FaixaDeSuporte } from './faixa-de-suporte';
import { BotaoDeAjuda } from '../ajuda/botao-de-ajuda';
import { TourDaArea } from '../ajuda/tour';
import { Sino } from './sino';

interface ItemMenu {
  para: string;
  rotulo: string;
  icone: typeof Home;
  /**
   * Sem esta permissão, o item não aparece. Menu que leva a uma tela de "sem
   * permissão" é botão morto — e a checagem que vale continua sendo a da API.
   */
  permissao?: PermissionKey;
  /** Destinos ainda não implementados aparecem, mas avisam que estão por vir. */
  emBreve?: boolean;
  /** A permissão vale em qualquer escopo (sala, responsável), não só no tenant. */
  emQualquerEscopo?: boolean;
  /**
   * Recurso do plano (seção 11.1). Sem ele, o item some: o menu não leva a uma
   * tela que responde 402 — quem quiser conhecer o recurso encontra na tela de
   * planos, que é onde ele é vendido.
   */
  recurso?: RecursoDoPlano;
  /**
   * Só para quem é responsável de criança: quem trabalha no infantil já tem a
   * tela da sala, e não precisa de mais um destino parecido no menu.
   */
  somenteResponsavel?: boolean;
  /** Só para a equipe da El Shaday: o escopo é de plataforma, e não de igreja. */
  somentePlataforma?: boolean;
  /**
   * Sai da barra principal no modo simplificado (seção 9.5).
   *
   * Não some do produto: continua atrás de "Mostrar tudo" e do "Mais" do
   * celular. Menos opções à vista é o pedido; esconder a tela seria outra
   * coisa, e deixaria gente sem saída.
   */
  secundario?: boolean;
  /**
   * O grupo em que o item aparece na barra lateral.
   *
   * Sem rótulo, doze destinos em sequência única obrigam a ler a lista inteira
   * para achar um. O agrupamento não esconde nada e não muda permissão — só diz
   * de que assunto cada trecho trata, como "Configurações" já fazia embaixo.
   *
   * Os itens pessoais ficam sem grupo, no topo: são os que quem serve abre
   * todo domingo, e não devem estar atrás de um rótulo.
   */
  grupo?: 'A igreja' | 'Infantil' | 'Conteúdo';
}

const MENU: ItemMenu[] = [
  { para: '/inicio', rotulo: 'Início', icone: Home },
  // Quem serve entra aqui primeiro: é a tela mais usada do produto (D02).
  { para: '/minhas-escalas', rotulo: 'Minhas escalas', icone: CalendarCheck },
  { grupo: 'A igreja', para: '/agenda', rotulo: 'Agenda', icone: CalendarDays, permissao: 'events:read' },
  { grupo: 'A igreja', para: '/pessoas', rotulo: 'Pessoas', icone: Users, permissao: 'people:read' },
  { grupo: 'A igreja', para: '/escalas', rotulo: 'Escalas', icone: ScrollText, permissao: 'assignments:read' },
  {
    para: '/departamentos',
    grupo: 'A igreja',
    rotulo: 'Equipes',
    icone: LayoutGrid,
    permissao: 'ministries:read',
    secundario: true,
  },
  /*
   * O professor do kids tem `children:read` no escopo da sala, e o responsável
   * no escopo dele: nenhum dos dois passa por uma checagem de tenant, e os dois
   * precisam do menu.
   */
  {
    para: '/kids',
    recurso: 'kids',
    grupo: 'Infantil',
    rotulo: 'Infantil',
    icone: Baby,
    permissao: 'children:read',
    emQualquerEscopo: true,
  },
  /*
   * A porta do infantil é trabalho de domingo, e quem opera a estação não é
   * quem administra o cadastro: ela tem entrada própria no menu.
   */
  {
    para: '/kids/estacao',
    recurso: 'kids',
    grupo: 'Infantil',
    rotulo: 'Porta do kids',
    icone: DoorOpen,
    permissao: 'child_checkins:create',
    emQualquerEscopo: true,
  },
  {
    para: '/kids/meus-filhos',
    recurso: 'kids',
    rotulo: 'Meus filhos',
    icone: Baby,
    permissao: 'child_checkins:read',
    emQualquerEscopo: true,
    somenteResponsavel: true,
  },
  {
    para: '/repertorio',
    recurso: 'roteiro',
    grupo: 'Conteúdo',
    rotulo: 'Repertório',
    icone: Music,
    permissao: 'songs:read',
    secundario: true,
  },
  {
    para: '/comunicados',
    grupo: 'Conteúdo',
    rotulo: 'Comunicados',
    icone: Megaphone,
    permissao: 'notifications:read',
    secundario: true,
  },
  {
    para: '/relatorios',
    recurso: 'relatorios',
    grupo: 'Conteúdo',
    rotulo: 'Relatórios',
    icone: BarChart3,
    permissao: 'reports:read',
    secundario: true,
  },
];

/** Fica fora da barra principal: é configuração, não trabalho do dia a dia. */
const MENU_CONFIGURACOES: ItemMenu[] = [
  { para: '/minha-disponibilidade', rotulo: 'Minha agenda', icone: CalendarClock },
  { para: '/configuracoes/acessibilidade', rotulo: 'Como você enxerga', icone: Accessibility },
  { para: '/configuracoes/seguranca', rotulo: 'Segurança da conta', icone: ShieldCheck },
  { para: '/ajuda', rotulo: 'Central de ajuda', icone: CircleHelp },
  { para: '/configuracoes/notificacoes', rotulo: 'Notificações', icone: Bell },
  {
    para: '/configuracoes/igreja',
    rotulo: 'Dados da igreja',
    icone: Church,
    permissao: 'settings:read',
  },
  {
    para: '/configuracoes/congregacoes',
    rotulo: 'Congregações',
    icone: Building2,
    permissao: 'settings:read',
  },
  {
    para: '/configuracoes/whatsapp',
    recurso: 'whatsapp',
    rotulo: 'WhatsApp',
    icone: MessageCircle,
    permissao: 'whatsapp:read',
  },
  {
    para: '/assinatura',
    rotulo: 'Assinatura',
    icone: CreditCard,
    permissao: 'billing:read',
  },
  {
    para: '/backoffice',
    rotulo: 'Backoffice',
    icone: ShieldAlert,
    // Só a equipe da El Shaday. Não é permissão de igreja nenhuma.
    somentePlataforma: true,
  },
  {
    para: '/configuracoes/auditoria',
    rotulo: 'Auditoria',
    icone: Settings,
    permissao: 'audit:read',
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const {
    usuario,
    igreja,
    igrejas,
    trocarIgreja,
    sair,
    pode,
    podeEmAlgumEscopo,
    permissoes,
    temRecurso,
  } = useSessao();
  const navegar = useNavigate();
  const [tema, setTema] = useState<Tema>(() => temaAtual());
  const [trocandoIgreja, setTrocandoIgreja] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const [verTudo, setVerTudo] = useState(false);

  useEffect(() => {
    // A cor da marca da igreja vale para toda a interface.
    aplicarCorDaMarca(null);
  }, [igreja?.id]);

  const ehSomenteResponsavel = !permissoes.some(
    (concessao) => concessao.scope.kind !== 'guardian' && concessao.scope.kind !== 'self',
  );

  const podeVer = (item: ItemMenu) => {
    if (item.somentePlataforma) return usuario?.isPlatformUser === true;
    if (item.somenteResponsavel && !ehSomenteResponsavel) return false;
    if (item.recurso && !temRecurso(item.recurso)) return false;
    if (!item.permissao) return true;

    return item.emQualquerEscopo ? podeEmAlgumEscopo(item.permissao) : pode(item.permissao);
  };

  const permitidos = (itens: ItemMenu[]) => itens.filter(podeVer);

  /*
   * Modo simplificado: menos opções à vista (seção 9.5).
   *
   * O que sai da barra continua alcançável — "Mostrar tudo" no computador e
   * "Mais" no celular. Quem liga o modo quer uma tela mais calma, e não um
   * produto menor.
   */
  const simplificado = usuario?.modoSimplificado === true;
  const doDiaADia = permitidos(MENU).filter((item) => !simplificado || verTudo || !item.secundario);
  const guardados = permitidos(MENU).filter((item) => !doDiaADia.includes(item));

  // Celular: quatro atalhos na barra, e o resto atrás de "Mais".
  const principaisNoCelular = doDiaADia.slice(0, 4);
  const restanteNoCelular = [
    ...doDiaADia.slice(4),
    ...guardados,
    ...permitidos(MENU_CONFIGURACOES),
  ];

  return (
    <div className="min-h-dvh bg-[var(--cor-fundo)]">
      {/*
        O primeiro Tab de qualquer tela: quem navega por teclado ou leitor de
        tela não deve percorrer o menu inteiro a cada página (seção 9.5).
      */}
      <a
        href="#conteudo"
        data-testid="pular-para-o-conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:inline-flex focus:h-11 focus:items-center focus:rounded-[var(--radius-md)] focus:bg-[var(--brand)] focus:px-4 focus:text-sm focus:font-medium focus:text-[var(--brand-texto)]"
      >
        Pular para o conteúdo
      </a>

      {/* Antes de tudo: ninguém pode esquecer que há suporte dentro da conta. */}
      <TourDaArea />
      <FaixaDeSuporte />
      <FaixaDaAssinatura />

      <div className="mx-auto flex w-full max-w-[1440px]">
        {/* Desktop: barra lateral */}
        <aside
          data-testid="nav-lateral"
          className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-[var(--cor-borda)] bg-[var(--cor-superficie)] lg:flex"
        >
          <div className="flex items-center gap-2.5 px-5 py-5">
            <div className="flex size-9 items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand)] text-sm font-bold text-[var(--brand-texto)]">
              L
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{igreja?.name ?? 'Levitas'}</p>
              <p className="truncate text-xs text-[var(--cor-texto-suave)]">Levitas</p>
            </div>
          </div>

          <nav aria-label="Navegação principal" className="flex flex-1 flex-col gap-0.5 px-3">
            {/*
              * O rótulo do grupo sai do próprio item, e por isso ele nunca fica
              * órfão: `doDiaADia` já veio filtrado por permissão, então um
              * grupo sem nenhum destino permitido simplesmente não existe aqui.
              * Escrever os grupos como lista fixa deixaria "Infantil" pairando
              * sozinho para quem não trabalha no infantil.
              */}
            {doDiaADia.map((item, indice) => (
              <Fragment key={item.para}>
                {item.grupo && item.grupo !== doDiaADia[indice - 1]?.grupo ? (
                  <p className="px-3 pb-1 pt-3 text-2xs font-medium uppercase tracking-wide text-[var(--cor-texto-suave)]">
                    {item.grupo}
                  </p>
                ) : null}
                <ItemLateral item={item} />
              </Fragment>
            ))}

            {guardados.length > 0 ? (
              <button
                type="button"
                onClick={() => setVerTudo(true)}
                data-testid="mostrar-tudo"
                className="flex h-11 items-center gap-2.5 rounded-[var(--radius-md)] px-3 text-sm font-medium text-[var(--cor-texto-suave)] hover:bg-[var(--cor-superficie-2)] hover:text-[var(--cor-texto)]"
              >
                <Menu aria-hidden className="size-4 shrink-0" />
                <span className="truncate">Mostrar tudo</span>
              </button>
            ) : null}
          </nav>

          <div className="flex flex-col gap-0.5 border-t border-[var(--cor-borda)] p-3">
            <p className="px-3 pb-1 text-2xs font-medium uppercase tracking-wide text-[var(--cor-texto-suave)]">
              Configurações
            </p>
            {permitidos(MENU_CONFIGURACOES).map((item) => (
              <ItemLateral key={item.para} item={item} />
            ))}
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-[var(--cor-borda)] bg-[var(--cor-superficie)]/95 px-4 backdrop-blur sm:px-6">
            <div className="flex min-w-0 items-center gap-2 lg:hidden">
              <div className="flex size-8 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--brand)] text-xs font-bold text-[var(--brand-texto)]">
                L
              </div>
              <span className="truncate text-sm font-semibold">{igreja?.name}</span>
            </div>

            <div className="hidden min-w-0 lg:block">
              {igrejas.length > 1 ? (
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-[var(--cor-texto-suave)]">Igreja:</span>
                  <span className="relative">
                    <select
                      aria-label="Trocar de igreja"
                      className="h-9 appearance-none rounded-[var(--radius-md)] border border-[var(--cor-borda-forte)] bg-[var(--cor-superficie)] pl-3 pr-8 text-sm"
                      value={igreja?.id ?? ''}
                      disabled={trocandoIgreja}
                      onChange={async (evento) => {
                        setTrocandoIgreja(true);
                        try {
                          await trocarIgreja(evento.target.value);
                        } finally {
                          setTrocandoIgreja(false);
                        }
                      }}
                    >
                      {igrejas.map((opcao) => (
                        <option key={opcao.id} value={opcao.id}>
                          {opcao.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      aria-hidden
                      className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-[var(--cor-texto-suave)]"
                    />
                  </span>
                </label>
              ) : null}
            </div>

            <div className="flex items-center gap-1.5">
              {/* A ajuda vem antes do sino: quem está perdido precisa dela agora. */}
              <BotaoDeAjuda />
              <Sino />

              <Button
                variante="fantasma"
                tamanho="icone"
                aria-label={tema === 'escuro' ? 'Usar tema claro' : 'Usar tema escuro'}
                onClick={() => setTema(alternarTema())}
              >
                {tema === 'escuro' ? (
                  <Sun aria-hidden className="size-4" />
                ) : (
                  <Moon aria-hidden className="size-4" />
                )}
              </Button>

              <div className="hidden items-center gap-2 sm:flex">
                <span className="max-w-[12rem] truncate text-sm text-[var(--cor-texto-suave)]">
                  {usuario?.name}
                </span>
              </div>

              <Button
                variante="fantasma"
                tamanho="icone"
                aria-label="Sair da conta"
                onClick={async () => {
                  await sair();
                  navegar('/entrar', { replace: true });
                }}
              >
                <LogOut aria-hidden className="size-4" />
              </Button>
            </div>
          </header>

          <main id="conteudo" tabIndex={-1} className="flex-1 px-4 pb-24 pt-5 outline-none sm:px-6 lg:pb-10">
            {children}
          </main>
        </div>
      </div>

      {/* Celular: barra inferior, ao alcance do polegar (seção 9.3) */}
      <nav
        aria-label="Navegação principal"
        data-testid="nav-inferior"
        className="safe-bottom fixed inset-x-0 bottom-0 z-30 flex border-t border-[var(--cor-borda)] bg-[var(--cor-superficie)] lg:hidden"
      >
        {principaisNoCelular.map((item) => (
          <ItemInferior key={item.para} item={item} />
        ))}

        {/* O que não coube: sem isto, Configurações não teria como ser
            alcançado no celular — e a maioria da igreja só usa o celular. */}
        {restanteNoCelular.length > 0 ? (
          <button
            type="button"
            data-testid="menu-mais"
            aria-haspopup="dialog"
            aria-expanded={menuAberto}
            onClick={() => setMenuAberto(true)}
            className="flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-2xs font-medium text-[var(--cor-texto-suave)]"
          >
            <Menu aria-hidden className="size-5" />
            <span className="truncate px-1">Mais</span>
          </button>
        ) : null}
      </nav>

      <Modal aberto={menuAberto} aoFechar={() => setMenuAberto(false)} titulo="Mais">
        <nav aria-label="Mais destinos" className="flex flex-col gap-0.5 p-2">
          {restanteNoCelular.map((item) => (
            <ItemLateral key={item.para} item={item} aoNavegar={() => setMenuAberto(false)} />
          ))}
        </nav>
      </Modal>
    </div>
  );
}

function ItemLateral({ item, aoNavegar }: { item: ItemMenu; aoNavegar?: () => void }) {
  const Icone = item.icone;
  return (
    <NavLink
      to={item.para}
      aria-disabled={item.emBreve || undefined}
      onClick={(evento) => {
        if (item.emBreve) {
          evento.preventDefault();
          return;
        }
        aoNavegar?.();
      }}
      className={({ isActive }) =>
        cn(
          'flex h-11 items-center gap-2.5 rounded-[var(--radius-md)] px-3 text-sm font-medium transition-colors',
          isActive && !item.emBreve
            ? 'bg-[var(--brand-suave)] text-[var(--brand-contraste)]'
            : 'text-[var(--cor-texto-suave)] hover:bg-[var(--cor-superficie-2)] hover:text-[var(--cor-texto)]',
          item.emBreve && 'cursor-default opacity-60',
        )
      }
    >
      <Icone aria-hidden className="size-4 shrink-0" />
      <span className="truncate">{item.rotulo}</span>
      {item.emBreve ? (
        <Badge status="neutro" className="ml-auto text-2xs">
          em breve
        </Badge>
      ) : null}
    </NavLink>
  );
}

function ItemInferior({ item }: { item: ItemMenu }) {
  const Icone = item.icone;
  return (
    <NavLink
      to={item.para}
      aria-disabled={item.emBreve || undefined}
      onClick={(evento) => item.emBreve && evento.preventDefault()}
      className={({ isActive }) =>
        cn(
          'flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-2xs font-medium',
          isActive && !item.emBreve ? 'text-[var(--brand)]' : 'text-[var(--cor-texto-suave)]',
          item.emBreve && 'opacity-50',
        )
      }
    >
      <Icone aria-hidden className="size-5" />
      <span className="truncate px-1">{item.rotulo}</span>
    </NavLink>
  );
}
